import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash-es';
import { AppThunk, RootState } from 'store';
import { currentUserSelector } from 'modules/user/userSlice';
import {
  allLifestyleSubscriptions,
  createSubscription,
  personalCarSubscriptionSelector,
  petSubscriptionSelector,
  updateSubscription,
} from 'modules/subscriptions/subscriptionSlice';
import { getPlan, plans } from 'utils/planMapper';
import createCarbonApi from 'api/carbonOffset';
import { normalizeItems } from 'modules/subscriptions/employeeBenefitSlice';
import createPersonalSubscriptionApi from 'api/personalSubscription';
import createEmployeeBenefitApi from 'api/employeeBenefit';
import { syncAttribution } from 'modules/user/attributionSlice';
import { showToast } from 'modules/app/toasterSlice';
import { appContextSelector, supportedCountriesISO2Selector } from 'modules/app/appSlice';
import { defaultPortfolioSelector } from 'modules/projects/projectsSlice';
import { setTag, tags } from 'utils/tags';
import { roundNumberToTwo } from 'utils/numberFormatter';

type ProcessType = 'create' | 'update' | 'cancel';

export type SubscriptionType = 'lifestyle' | 'cars' | 'pets';

export type GroupType = 'Individual' | 'Couple' | 'Family';

export type SubscriptionPeriod = 'monthly' | 'twiceYearly' | 'yearly';

export const getPeriodMultiplier = (period?: SubscriptionPeriod): number => {
  switch (period) {
    case 'monthly':
      return 1;
    case 'twiceYearly':
      return 6;
    case 'yearly':
      return 12;
    default:
      return 1;
  }
};

export const getPriceForSubscriptionPeriod = (price: number, period?: SubscriptionPeriod): number => price * getPeriodMultiplier(period);

interface SubscriptionSection {
  items: SubscriptionItem[];
  clonedItems: SubscriptionItem[];
  subscriptionId?: string;
  selectedPlanId: string;
  subscriptionPeriod: SubscriptionPeriod;
  itemCount: number;
  currency: string;
  process: ProcessType;
  changeCount: number;
}

interface LifestyleSubscriptionSection extends SubscriptionSection {
  groupType: GroupType;
}

interface State {
  selectedSubscriptions: {
    lifestyle: number;
    pets: number;
    cars: number;
  };
  lifestyle: LifestyleSubscriptionSection[];
  cars: SubscriptionSection[];
  pets: SubscriptionSection[];
  latestOrderDate?: string;
  process: ProcessType;
  cancelAtPeriodEnd?: boolean;
  isLoading: boolean;
  isEditing: boolean;
  orderId?: string;
  orderDate?: string;
}

const emptySubSection: SubscriptionSection = {
  items: [],
  clonedItems: [],
  selectedPlanId: 'ClimatePositive',
  itemCount: 1,
  currency: '',
  subscriptionPeriod: 'monthly',
  process: 'create',
  changeCount: 0,
};

const initialState: State = {
  selectedSubscriptions: {
    lifestyle: 0,
    cars: 0,
    pets: 0,
  },
  lifestyle: [
    {
      ...emptySubSection,
      groupType: 'Individual',
    },
  ],
  cars: [
    {
      ...emptySubSection,
    },
  ],
  pets: [
    {
      ...emptySubSection,
    },
  ],
  process: 'create',
  isLoading: false,
  isEditing: false,
  orderId: '',
  orderDate: '',
};

interface EditProps {
  index: number;
  value: number;
  price?: number;
  vat?: number;
}

interface CreateProps {
  price?: number;
  vat?: number;
  kilosCo2?: number;
  plan?: string;
  groupType?: string;
  personCount?: number;
  countryName?: string;
  countryCode?: string;
  multiplier?: number;
  years?: number;
  months?: number;
  callback?: () => void;
}

interface CountryProps {
  countryName: string;
  countryCode: string;
}

interface PlanProps {
  index: number;
  plan: string;
  type: SubscriptionType;
}

interface SubscriptionPayloadHeader {
  interval: string;
  category: string;
  type: string;
  description: string;
}

const lifestyleHeader = (persons: number, subscriptionPeriod: SubscriptionPeriod): SubscriptionPayloadHeader => ({
  interval: subscriptionPeriod,
  category: 'Lifestyle',
  type: 'PersonalLifestyle',
  description: `Lifestyle subscription for ${persons} person${persons > 1 ? 's' : ''}`,
});

const petsHeader = (pets: number, subscriptionPeriod: SubscriptionPeriod): SubscriptionPayloadHeader => ({
  interval: subscriptionPeriod,
  category: 'Pets',
  type: 'PersonalPets',
  description: `Pet subscription for ${pets} pet${pets > 1 ? 's' : ''}`,
});

const carsHeader = (car: number, subscriptionPeriod: SubscriptionPeriod): SubscriptionPayloadHeader => ({
  interval: subscriptionPeriod,
  category: 'Car',
  type: 'PersonalCar',
  description: `Car subscription for ${car} car${car > 1 ? 's' : ''}`,
});

const slice = createSlice({
  name: 'personalSubscription',
  initialState,
  reducers: {
    setSelectedSubscription: (state: State, action: PayloadAction<{ index: number; type: SubscriptionType }>): State => {
      const { type, index } = action.payload;
      state.selectedSubscriptions[type] = index;
      return state;
    },
    reset: (): State => {
      return { ...initialState };
    },
    resetWithoutDisablingEditing: (state: State): State => {
      const { selectedSubscriptions } = state;
      const lifestyleCurrency = state.lifestyle[selectedSubscriptions.lifestyle].currency;
      const petsCurrency = state.pets[selectedSubscriptions.pets].currency;
      const carsCurrency = state.cars[selectedSubscriptions.cars].currency;
      return {
        ...initialState,
        isEditing: true,
        lifestyle: [{ ...emptySubSection, groupType: 'Individual', currency: lifestyleCurrency }],
        cars: [{ ...emptySubSection, currency: carsCurrency }],
        pets: [{ ...emptySubSection, currency: petsCurrency }],
      };
    },
    setLifestyleSubscriptions: (state: State, action: PayloadAction<SubscriptionObject[]>): State => {
      action.payload.forEach((sub, index) => {
        const months = sub.items[0].details.person?.months;
        const years = sub.items[0].details.person?.years;
        let subscriptionPeriod: SubscriptionPeriod = 'monthly';

        if (months === 1) {
          subscriptionPeriod = 'monthly';
        } else if (months === 6) {
          subscriptionPeriod = 'twiceYearly';
        } else if (years === 1) {
          subscriptionPeriod = 'yearly';
        }
        state.lifestyle[index] = {
          items: sub.items,
          clonedItems: cloneDeep(sub.items),
          subscriptionId: sub.id,
          currency: sub.currency ?? 'usd',
          itemCount: sub.items[0].details.person?.personCount ?? 1,
          selectedPlanId: sub.items[0].details.person?.plan ?? 'ClimatePositive',
          groupType: (sub.items[0].details.person?.groupType as GroupType) ?? 'Individual',
          subscriptionPeriod,
          process: 'update',
          changeCount: 0,
        };
      });
      return state;
    },
    setCarSubscription: (state: State, action: PayloadAction<SubscriptionObject>): State => {
      const {
        selectedSubscriptions: { cars: carindex },
      } = state;
      state.cars[carindex].items = action.payload.items;
      state.cars[carindex].clonedItems = cloneDeep(state.cars[carindex].items);
      state.cars[carindex].subscriptionId = action.payload.id;
      state.cars[carindex].currency = action.payload.currency ?? 'usd';
      state.cars[carindex].itemCount = action.payload.items[0].details.car?.cars ?? 1;
      // TODO: Set selected plan id and sub period
      return state;
    },
    setPetsSubscription: (state: State, action: PayloadAction<SubscriptionObject>): State => {
      const {
        selectedSubscriptions: { pets: petIndex },
      } = state;

      state.pets[petIndex].items = action.payload.items;
      state.pets[petIndex].clonedItems = cloneDeep(state.pets[petIndex].items);
      state.pets[petIndex].subscriptionId = action.payload.id;
      state.pets[petIndex].currency = action.payload.currency ?? 'usd';
      state.pets[petIndex].itemCount = action.payload.items[0].details.pets?.pets ?? 0;
      // TODO: Set selected plan id and sub period
      return state;
    },
    updateItemCO2: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value, price, vat } = action.payload;
      const {
        selectedSubscriptions: { lifestyle: lifestyleIndex },
      } = state;
      const item = state.lifestyle[lifestyleIndex].items[index];
      item.kilosCo2 = value;
      item.price = price || 0;
      item.vat = vat;
      return state;
    },
    updateSubPersonCount: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const {
        selectedSubscriptions: { lifestyle: lifestyleIndex },
      } = state;
      const item = state.lifestyle[lifestyleIndex].items[index];

      if (item?.details?.person) {
        item.details.person.personCount = value;
      }

      return state;
    },
    updateSelectedPersonCount: (state: State, action: PayloadAction<number>): State => {
      const {
        selectedSubscriptions: { lifestyle: lifestyleIndex },
      } = state;
      state.lifestyle[lifestyleIndex].itemCount = action.payload;
      return state;
    },
    updatePlanState: (state: State, action: PayloadAction<PlanProps>): State => {
      const { index, plan, type } = action.payload;
      const subIndex = state.selectedSubscriptions[type];

      const item = state[type][subIndex].items[index];
      if (item.details.person) {
        item.details.person.plan = plan;
      }
      item.multiplier = getPlan(plan)?.multiplier;
      return state;
    },
    setLifestyleSubscriptionItem: (state: State, action: PayloadAction<{ props: CreateProps; index?: number }>): State => {
      const {
        selectedSubscriptions: { lifestyle: lifestyleIndex },
      } = state;

      const { props, index } = action.payload;

      const targetSubIndex = index ?? lifestyleIndex;

      if (!state.lifestyle[targetSubIndex]) {
        state.lifestyle[targetSubIndex] = { ...emptySubSection, groupType: 'Individual' };

        if (index) {
          state.selectedSubscriptions.lifestyle = index;
        }
      }

      const item = {
        portfolioid: 'defaultco2',
        kilosCo2: props.kilosCo2 || 1000,
        price: props.price || 0,
        vat: props.vat,
        multiplier: props.multiplier || plans[0].multiplier,
        details: {
          person: {
            category: 'Lifestyle',
            countryCode: props.countryCode || 'NO',
            countryName: props.countryName || 'Norway',
            plan: props.plan || state.lifestyle[targetSubIndex].selectedPlanId || plans[0].id,
            groupType: props.groupType || 'Individual',
            days: 0,
            months: props.months ?? 1,
            years: props.years ?? 0,
            personCount: props.personCount || 1,
          },
        },
      };

      state.lifestyle[targetSubIndex].items = [item];

      return state;
    },
    refreshItems: (state: State): State => {
      // If there's no id on the first element, this means that there are no saved subscription and
      // this one is a freshly created item for editing. In this case, we shouldn't delete it
      const areNoSavedSubscriptions = !state.lifestyle[0].subscriptionId;
      state.lifestyle = state.lifestyle
        .filter(sub => sub.subscriptionId || areNoSavedSubscriptions)
        .map(sub => {
          sub.items = cloneDeep(sub.clonedItems);
          return sub;
        });

      state.selectedSubscriptions.lifestyle = 0;
      state.cars[0].items = cloneDeep(state.cars[0].clonedItems);
      state.pets[0].items = cloneDeep(state.pets[0].clonedItems);
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setSelectedPlanId: (state: State, action: PayloadAction<{ plan: string; type: SubscriptionType }>): State => {
      const { plan, type } = action.payload;
      const {
        selectedSubscriptions: { lifestyle: lifestyleIndex },
      } = state;

      state[type][lifestyleIndex].selectedPlanId = plan;
      return state;
    },
    setProcess: (state: State, action: PayloadAction<{ processType?: ProcessType; subType: SubscriptionType }>): State => {
      const { processType, subType } = action.payload;
      const subIndex = state.selectedSubscriptions[subType];
      if (processType) {
        state[subType][subIndex].process = processType;
        return state;
      }

      if (state[subType][subIndex].subscriptionId) {
        if (state[subType][subIndex].items.length) {
          state[subType][subIndex].process = 'update';
        } else {
          state[subType][subIndex].process = 'cancel';
        }
      } else {
        state[subType][subIndex].process = 'create';
      }

      return state;
    },
    setCurrency: (state: State, action: PayloadAction<{ currency: string; type: SubscriptionType }>): State => {
      const { type, currency } = action.payload;
      // const subIndex = state.selectedSubscriptions[type];

      state[type].forEach(sub => {
        sub.currency = currency;
      });

      // state[type][subIndex].currency = currency;
      return state;
    },

    setGroupType: (state: State, action: PayloadAction<GroupType>): State => {
      const {
        selectedSubscriptions: { lifestyle: lifestyleIndex },
      } = state;
      state.lifestyle[lifestyleIndex].groupType = action.payload;
      if (state.lifestyle[lifestyleIndex] && state.lifestyle[lifestyleIndex].items[0]?.details?.person) {
        // @ts-ignore
        state.lifestyle[lifestyleIndex].items[0].details.person.groupType = action.payload;
      }
      return state;
    },
    setSubscriptionPeriod: (state: State, action: PayloadAction<{ type: SubscriptionType; period: SubscriptionPeriod }>): State => {
      const { type, period } = action.payload;
      const subIndex = state.selectedSubscriptions[type];

      state[type][subIndex].subscriptionPeriod = period;

      return state;
    },
    setEditing: (state: State, action: PayloadAction<boolean>): State => {
      state.isEditing = action.payload;
      return state;
    },
    setLifestyleSubscriptionTime: (state: State, action: PayloadAction<{ months?: number; years?: number }>): State => {
      const { months, years } = action.payload;
      const {
        selectedSubscriptions: { lifestyle: lifestyleIndex },
      } = state;

      if (state.lifestyle[lifestyleIndex].items.length && state.lifestyle[lifestyleIndex].items[0].details.person) {
        // @ts-ignore
        state.lifestyle[lifestyleIndex].items[0].details.person.months = months ?? 0;
        // @ts-ignore
        state.lifestyle[lifestyleIndex].items[0].details.person.years = years ?? 0;
      }

      return state;
    },
    setOrder: (state: State, action: PayloadAction<{ id: string; date: string }>): State => {
      state.orderId = action.payload.id;
      state.orderDate = action.payload.date;
      return state;
    },
    clearOrder: (state: State): State => {
      state.orderId = '';
      state.orderDate = '';
      return state;
    },

    increaseChangeCount: (state: State, action: PayloadAction<SubscriptionType>): State => {
      const type = action.payload;
      const subIndex = state.selectedSubscriptions[type];

      state[type][subIndex].changeCount++;
      return state;
    },
    clearChangeCount: (state: State, action: PayloadAction<SubscriptionType>): State => {
      const type = action.payload;
      const subIndex = state.selectedSubscriptions[type];

      if (state[type][subIndex]) {
        state[type][subIndex].changeCount = 0;
      }
      return state;
    },
    setLifestyleCountry: (state: State, action: PayloadAction<CountryProps>): State => {
      const { countryCode, countryName } = action.payload;
      const {
        selectedSubscriptions: { lifestyle: lifestyleIndex },
      } = state;

      const item = state.lifestyle[lifestyleIndex].items[0];
      if (item.details.person) {
        item.details.person.countryCode = countryCode;
        item.details.person.countryName = countryName;
      }
      return state;
    },
  },
});

const {
  reset,
  resetWithoutDisablingEditing,
  setLoading,
  setSelectedPlanId,
  setLifestyleSubscriptions,
  setCarSubscription,
  setPetsSubscription,
  setCurrency,
  updateItemCO2,
  updatePlanState,
  updateSubPersonCount,
  updateSelectedPersonCount,
  setLifestyleSubscriptionItem,
  setGroupType,
  setSubscriptionPeriod,
  setProcess,
  setEditing,
  refreshItems,
  setLifestyleSubscriptionTime,
  setOrder,
  increaseChangeCount,
  clearChangeCount,
  setLifestyleCountry,
  clearOrder,
  setSelectedSubscription,
} = slice.actions;
export {
  reset,
  resetWithoutDisablingEditing,
  setGroupType,
  setProcess,
  refreshItems,
  setLifestyleSubscriptionTime,
  setOrder,
  increaseChangeCount,
  clearChangeCount,
  setLifestyleCountry,
  clearOrder,
  setSelectedSubscription,
};
export default slice.reducer;

export const setSubscriptions = (normalizedSubscriptions: SubscriptionObject[], type: SubscriptionType): AppThunk => (dispatch): void => {
  switch (type) {
    case 'lifestyle':
      dispatch(setLifestyleSubscriptions(normalizedSubscriptions));
      break;
    case 'cars':
      dispatch(setCarSubscription(normalizedSubscriptions[0]));
      break;
    case 'pets':
      dispatch(setPetsSubscription(normalizedSubscriptions[0]));
      break;
  }
};

export const preparePersonalSubscriptions = (subscriptions: SubscriptionObject[], subscriptionType: SubscriptionType): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const currentUser = currentUserSelector(getState());

  try {
    if (subscriptions.length) {
      const normalizedSubscriptions = await Promise.all(
        subscriptions.map(async subscription => {
          const normalizedSubscription = cloneDeep(subscription);
          for (const item of normalizedSubscription.items) {
            let data;
            if (item.details.person) {
              const multiplier = getPlan(item.details.person.plan)?.multiplier || plans[0].multiplier;
              data = await carbonApi.getCarbonOffsetPrice(
                {
                  kilosCo2: item.kilosCo2,
                  monthlyPersonalLifestylePersons:
                    (item.details.person?.personCount ?? 0) * getPeriodMultiplier(subscription.interval as SubscriptionPeriod),
                },
                currentUser?.customerId ?? '',
              );
              item.multiplier = multiplier;
              item.kilosCo2 = item.kilosCo2 / item.multiplier;
            }

            if (item.details.car) {
              data = await carbonApi.getCarbonOffsetPrice(
                {
                  kilosCo2: item.kilosCo2,
                  monthlyPersonalCars: (item.details.car.cars ?? 0) * getPeriodMultiplier(subscription.interval as SubscriptionPeriod),
                },
                currentUser?.customerId ?? '',
              );
            }

            if (item.details.pets) {
              data = await carbonApi.getCarbonOffsetPrice(
                {
                  kilosCo2: item.kilosCo2,
                  monthlyPersonalCars: (item.details.pets.pets ?? 0) * getPeriodMultiplier(subscription.interval as SubscriptionPeriod),
                },
                currentUser?.customerId ?? '',
              );
            }
            item.price = data?.salesPrice ?? 0;
            item.vat = data?.vat;
          }
          return normalizedSubscription;
        }),
      );

      dispatch(setSubscriptions(normalizedSubscriptions, subscriptionType));
    } else {
      const currency = currentUser?.customer?.payment?.currency ?? 'usd';
      dispatch(setCurrency({ currency, type: subscriptionType }));
    }
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:fetchPriceError' }));
  }
};

export const prepareLifestyleSubscription = (): AppThunk => (dispatch, getState): void => {
  const lifestyleSubs = allLifestyleSubscriptions(getState());
  dispatch(preparePersonalSubscriptions(lifestyleSubs, 'lifestyle'));
};

export const preparePetSubscription = (): AppThunk => (dispatch, getState): void => {
  const petSub = petSubscriptionSelector(getState());
  dispatch(preparePersonalSubscriptions(petSub ? [petSub] : [], 'pets'));
};

export const prepareCarSubscription = (): AppThunk => (dispatch, getState): void => {
  const carSub = personalCarSubscriptionSelector(getState());
  dispatch(preparePersonalSubscriptions(carSub ? [carSub] : [], 'cars'));
};

export const prepareAllSubscriptions = (): AppThunk => (dispatch): void => {
  dispatch(prepareLifestyleSubscription());
  dispatch(preparePetSubscription());
  dispatch(prepareCarSubscription());
};

export const savePersonalSubscription = (type: SubscriptionType, errorCallback?: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const sub = getState().personalSubscription;

  const { selectedSubscriptions } = sub;

  const subIndex = selectedSubscriptions[type];

  const { items, clonedItems, subscriptionId, process, subscriptionPeriod } = sub[type][subIndex];

  let subObjectCount = 0;
  let payloadHeader: SubscriptionPayloadHeader;

  switch (type) {
    case 'lifestyle':
      if (items.length) {
        subObjectCount = items[0].details.person?.personCount ?? 0;
      } else if (clonedItems.length) {
        subObjectCount = clonedItems[0].details.person?.personCount ?? 0;
      }
      payloadHeader = lifestyleHeader(subObjectCount, subscriptionPeriod);
      break;
    case 'cars':
      if (items.length) {
        subObjectCount = items[0].details.car?.cars ?? 0;
      } else if (clonedItems.length) {
        subObjectCount = clonedItems[0].details.car?.cars ?? 0;
      }
      payloadHeader = carsHeader(subObjectCount, subscriptionPeriod);
      break;
    case 'pets':
      if (items.length) {
        subObjectCount = items[0].details.pets?.pets ?? 0;
      } else if (clonedItems.length) {
        subObjectCount = clonedItems[0].details.pets?.pets ?? 0;
      }
      payloadHeader = petsHeader(subObjectCount, subscriptionPeriod);
      break;
    default:
      payloadHeader = lifestyleHeader(subObjectCount, subscriptionPeriod);
      break;
  }

  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const normalizedItems = normalizeItems(items);
  const normalizedClonedItems = normalizeItems(clonedItems);
  const context = appContextSelector(getState());
  const personalSubscriptionAPI = createPersonalSubscriptionApi(context);

  try {
    if (process === 'update') {
      if (subscriptionId) {
        const payload = {
          ...payloadHeader,
          items: normalizedItems.length ? normalizedItems : normalizedClonedItems,
        };

        const data = await personalSubscriptionAPI.update(subscriptionId, payload, currentUser?.customerId ?? '');
        dispatch(setOrder({ id: data.subscription.choooseId ?? '', date: data.subscription.startDate ?? '' }));
        dispatch(updateSubscription({ ...data.subscription }));
        dispatch(clearChangeCount(type));
        dispatch(syncAttribution());
      }
    } else if (process === 'cancel') {
      if (subscriptionId) {
        const data = await personalSubscriptionAPI.remove(subscriptionId, currentUser?.customerId ?? '');
        dispatch(updateSubscription({ ...data.subscription }));
        dispatch(clearChangeCount(type));
        dispatch(syncAttribution());
      }
    } else {
      const subscription = {
        ...payloadHeader,
        items: normalizedItems,
      };

      const data = await personalSubscriptionAPI.create(subscription, currentUser?.customerId ?? '');
      setTag({
        ...tags.portalOffset.placeOrder,
        categories: [data.subscription?.category ?? ''],
        transactionId: data.orderId,
        transactionTotal: data.subscription.totalPrice,
        currencyCode: data.subscription.currency,
        transactionProducts: data.subscription.items.map(item => ({
          category: data.subscription.category ?? '',
          name: 'Lifestyle offset',
          price: roundNumberToTwo(item.price),
          quantity: item.kilosCo2,
          sku: 'lifestyle-offset',
        })),
      });
      dispatch(setOrder({ id: data.subscription.choooseId ?? '', date: data.subscription.startDate ?? '' }));
      dispatch(createSubscription({ ...data.subscription }));
      dispatch(clearChangeCount(type));
      dispatch(syncAttribution());
    }
  } catch (err) {
    errorCallback && errorCallback();
    let descriptioni18nKey = process !== 'create' ? 'myOffset:updateError' : 'myOffset:createError';
    //@ts-ignore
    if (err.data === 'No payment method is available.') {
      descriptioni18nKey = 'myOffset:invalidPaymentMethodError';
    }
    dispatch(
      showToast({
        variant: 'error',
        titlei18nKey: 'myOffset:errorTitle',
        descriptioni18nKey,
      }),
    );
  } finally {
    dispatch(setLoading(false));
  }
};

export const updatePlan = (props: PlanProps): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const { index, plan } = props;
  const {
    selectedSubscriptions: { lifestyle: lifestyleIndex },
  } = getState().personalSubscription;
  const { items, subscriptionPeriod } = getState().personalSubscription.lifestyle[lifestyleIndex];
  const item = items[index];

  const multiplier = getPlan(plan)?.multiplier;
  const currentUser = currentUserSelector(getState());

  try {
    const kilosCo2 = item.kilosCo2 * (multiplier || plans[0].multiplier);
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2,
        monthlyPersonalLifestylePersons: (item.details.person?.personCount ?? 0) * getPeriodMultiplier(subscriptionPeriod),
      },
      currentUser?.customerId ?? '',
    );
    dispatch(updateItemCO2({ index, value: item.kilosCo2, price: priceData.salesPrice, vat: priceData.vat }));
    dispatch(updatePlanState({ ...props }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:planError' }));
  }
};

export const updateSelectedPlan = (plan: string, type: SubscriptionType): AppThunk => (dispatch, getState): void => {
  const subscriptions = getState().personalSubscription;
  const { items } = subscriptions[type][0];
  dispatch(setSelectedPlanId({ plan, type }));
  items.forEach((_, index) => {
    dispatch(updatePlan({ index, plan, type }));
  });
};

export const updatePersonPlan = (plan: string): AppThunk => (dispatch, getState): void => {
  const {
    selectedSubscriptions: { lifestyle: lifestyleIndex },
  } = getState().personalSubscription;
  const { selectedPlanId } = getState().personalSubscription.lifestyle[lifestyleIndex];
  if (selectedPlanId !== plan) {
    dispatch(updateSelectedPlan(plan, 'lifestyle'));
  }
};

export const updateItemPeople = (props: EditProps): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const employeeBenefitAPI = createEmployeeBenefitApi(context);
  const {
    selectedSubscriptions: { lifestyle: lifestyleIndex },
  } = getState().personalSubscription;
  const { items, subscriptionPeriod } = getState().personalSubscription.lifestyle[lifestyleIndex];
  const item = items[props.index];
  const currentUser = currentUserSelector(getState());
  try {
    const data = await employeeBenefitAPI.getPercapitaFootprint(
      item.details.person?.countryCode ?? 'NO',
      props.value * getPeriodMultiplier(subscriptionPeriod),
    );
    const kilosCo2 = data.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2: data.kilosCo2e * (item.multiplier || plans[0].multiplier),
        monthlyPersonalLifestylePersons: (props.value ?? 0) * getPeriodMultiplier(subscriptionPeriod),
      },
      currentUser?.customerId ?? '',
    );

    dispatch(updateItemCO2({ index: props.index, value: kilosCo2, price: priceData.salesPrice, vat: priceData.vat }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:personCountError' }));
  }
};

export const updatePersonCount = (count: number): AppThunk => (dispatch): void => {
  dispatch(updateSubPersonCount({ value: count, index: 0 }));
  dispatch(updateSelectedPersonCount(count));
  dispatch(updateItemPeople({ value: count, index: 0 }));
};

export const setupLifestyleSubscription = (props?: CreateProps, cb?: CallableFunction, index?: number): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const employeeBenefitAPI = createEmployeeBenefitApi(context);
  const {
    selectedSubscriptions: { lifestyle: lifestyleIndex },
  } = getState().personalSubscription;

  const { currentAccount } = getState().user;
  const availableCountries = supportedCountriesISO2Selector(getState());

  const countryCodeFromAccount = currentAccount?.customer?.info?.address?.countryCodeIso2;
  let countryCodeFromProps;

  const permissibleCountryCodeAlternatives = ['earth', 'eu'];

  if (
    availableCountries.find(c => c.value === props?.countryCode) ||
    permissibleCountryCodeAlternatives.includes(props?.countryCode?.toLowerCase() ?? '')
  ) {
    countryCodeFromProps = props?.countryCode;
  }

  const targetSubIndex = index ?? lifestyleIndex;
  const targetSub = getState().personalSubscription.lifestyle[targetSubIndex];

  const selectedPlanId = targetSub?.selectedPlanId ?? 'ClimatePositive';
  const subscriptionPeriod = targetSub?.subscriptionPeriod ?? 'monthly';
  const itemCount = targetSub?.itemCount ?? 1;

  const countryCode = countryCodeFromProps ?? countryCodeFromAccount ?? 'NO';

  let countryName = 'Norway';

  if (!props?.countryCode && countryCodeFromAccount) {
    countryName = availableCountries.find(c => c.value === countryCodeFromAccount)?.label ?? 'Norway';
  } else if (props?.countryName) {
    countryName = props.countryName;
  }

  const personCount = props?.personCount ?? itemCount ?? 1;

  const plan = getPlan(props?.plan ?? selectedPlanId);
  const currentUser = currentUserSelector(getState());
  const currency = currentUser?.customer?.payment?.currency ?? 'usd';

  try {
    if (plan) {
      const footprintData = await employeeBenefitAPI.getPercapitaFootprint(countryCode, personCount * getPeriodMultiplier(subscriptionPeriod));
      const kilosCo2 = footprintData.kilosCo2e;
      const multiplier = plan.multiplier || plans[0].multiplier;
      const defaultPortfolio = defaultPortfolioSelector(getState());
      if (!defaultPortfolio) {
        dispatch(setLoading(false));
        return;
      }
      const priceData = await carbonApi.getCarbonOffsetPrice(
        {
          kilosCo2: footprintData.kilosCo2e * multiplier,
          monthlyPersonalLifestylePersons: personCount * getPeriodMultiplier(subscriptionPeriod),
        },
        currentUser?.customerId ?? '',
        defaultPortfolio.portfolioData.id,
      );

      dispatch(
        setLifestyleSubscriptionItem({
          props: { ...props, multiplier, price: priceData.salesPrice, vat: priceData.vat, kilosCo2, countryCode, countryName },
          index: targetSubIndex,
        }),
      );
      cb && cb();
    } else {
      dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:lifestyleItemSetErrorPlan' }));
    }
    dispatch(setCurrency({ currency, type: 'lifestyle' }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:lifestyleItemSetError' }));
  }
};

export const addNewLifestyleSubscription = (): AppThunk => (dispatch, getState) => {
  const newIndex = getState().personalSubscription.lifestyle.length;
  dispatch(setupLifestyleSubscription(undefined, undefined, newIndex));
};

export const updateLifestyleSubscriptionPeriod = (period: SubscriptionPeriod): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const employeeBenefitAPI = createEmployeeBenefitApi(context);
  dispatch(setSubscriptionPeriod({ type: 'lifestyle', period }));
  dispatch(setLoading(true));
  const {
    selectedSubscriptions: { lifestyle: lifestyleIndex },
  } = getState().personalSubscription;

  const { items } = getState().personalSubscription.lifestyle[lifestyleIndex];
  const item = items[0];
  const multiplier = getPlan(item?.details.person?.plan)?.multiplier;
  const currentUser = currentUserSelector(getState());

  try {
    const personCount = item?.details.person?.personCount ?? 0;

    const footprintData = await employeeBenefitAPI.getPercapitaFootprint(
      item?.details.person?.countryCode ?? 'NO',
      personCount * getPeriodMultiplier(period),
    );

    const kilosCo2 = footprintData.kilosCo2e;

    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2: footprintData.kilosCo2e * (multiplier || plans[0].multiplier),
        monthlyPersonalLifestylePersons: personCount * getPeriodMultiplier(period),
      },
      currentUser?.customerId ?? '',
    );

    switch (period) {
      case 'monthly':
        dispatch(setLifestyleSubscriptionTime({ months: 1 }));
        break;
      case 'twiceYearly':
        dispatch(setLifestyleSubscriptionTime({ months: 6 }));
        break;
      case 'yearly':
        dispatch(setLifestyleSubscriptionTime({ years: 1 }));
    }

    dispatch(updateItemCO2({ index: 0, value: kilosCo2, price: priceData.salesPrice, vat: priceData.vat }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:changePeriodError' }));
  }
};

export const enableEditing = (): AppThunk => (dispatch): void => {
  dispatch(setEditing(true));
};

export const disableEditing = (): AppThunk => (dispatch): void => {
  dispatch(prepareLifestyleSubscription());
  dispatch(setEditing(false));
};

export const updateLifestyleItemCountry = (props: CountryProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const employeeBenefitAPI = createEmployeeBenefitApi(context);
  dispatch(setLoading(true));

  const {
    selectedSubscriptions: { lifestyle: lifestyleIndex },
  } = getState().personalSubscription;

  const { items, subscriptionPeriod } = getState().personalSubscription.lifestyle[lifestyleIndex];
  const item = items[0];
  const currentUser = currentUserSelector(getState());
  try {
    const data = await employeeBenefitAPI.getPercapitaFootprint(
      props.countryCode,
      (item.details.person?.personCount ?? 0) * getPeriodMultiplier(subscriptionPeriod),
    );
    const kilosCo2 = data.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2: data.kilosCo2e * (item.multiplier || plans[0].multiplier),
        monthlyPersonalLifestylePersons: (item.details.person?.personCount ?? 0) * getPeriodMultiplier(subscriptionPeriod),
      },
      currentUser?.customerId ?? '',
    );
    dispatch(setLifestyleCountry({ ...props }));
    dispatch(updateItemCO2({ index: 0, value: kilosCo2, price: priceData.salesPrice, vat: priceData.vat }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:changeCountryError' }));
  }
};

export const selectedLifestyleSubscription = createSelector(
  (state: RootState) => state.personalSubscription,
  subState => {
    const {
      lifestyle,
      selectedSubscriptions: { lifestyle: lifestyleIndex },
    } = subState;

    return lifestyle[lifestyleIndex];
  },
);

export const lifestyleSubscriptionItem = createSelector(
  (state: RootState) => state.personalSubscription,
  subState => {
    const {
      lifestyle,
      selectedSubscriptions: { lifestyle: lifestyleIndex },
    } = subState;
    return lifestyle[lifestyleIndex] && lifestyle[lifestyleIndex].items ? lifestyle[lifestyleIndex].items[0] : undefined;
  },
);

export const cancelChanges = (): AppThunk => (dispatch, getState): void => {
  const allSubs = allLifestyleSubscriptions(getState());
  dispatch(refreshItems());
  dispatch(clearChangeCount('lifestyle'));
  if (allSubs.length) {
    dispatch(disableEditing());
  } else {
    dispatch(resetWithoutDisablingEditing());
    dispatch(setupLifestyleSubscription());
  }
};

export const cancelChangesVisible = createSelector(
  (state: RootState) => state.personalSubscription,
  allLifestyleSubscriptions,
  selectedLifestyleSubscription,
  (personalSubs, allSubs, selectedSub) => {
    if (allSubs.length) {
      return personalSubs.isEditing;
    } else {
      return selectedSub?.changeCount > 0;
    }
  },
);
